import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Checkbox, Empty, Input, Modal, Spin, Tag } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import {
  useAdvisoryAiOrgs,
  useUpdateAdvisoryAiOrgInternalMemoryMutation,
} from '@/features/advisory-ai/hooks'
import type { IndustryProfileStatus } from '@/features/advisory-ai/types'
import { platformRefreshStatusTagColor } from '@/features/advisory-ai/utils'
import { useToast } from '@/contexts/ToastContext'
import { queryKeys } from '@/api/queryKeys'

export type SelectedCompany = { orgId: string; orgName: string }

type SelectionEntry = { orgName: string; status: IndustryProfileStatus }

const XERO_ACCOUNTING_TYPE_ID = 1

type CreateConsolidatedModalProps = {
  open: boolean
  clientId: string
  isCreating: boolean
  onCancel: () => void
  onCreate: (title: string, companies: SelectedCompany[]) => void
}

export function CreateConsolidatedModal({
  open,
  clientId,
  isCreating,
  onCancel,
  onCreate,
}: CreateConsolidatedModalProps) {
  const [title, setTitle] = useState('')
  const [search, setSearch] = useState('')
  /**
   * Keyed by orgId, holding the status as it was when the row was ticked. The
   * status cannot be read back off `rows` at validation time: that list is
   * server-filtered by `search`, so typing in the search box would drop a
   * profile-less company out of view and silently unblock Create.
   */
  const [selected, setSelected] = useState<Map<string, SelectionEntry>>(new Map())
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  // Rows filled during this sitting. The grid refetch that would report them as
  // 'filled' is a round trip, and this keeps the row from flicking back to the
  // warning in the meantime.
  const [justFilled, setJustFilled] = useState<Set<string>>(new Set())

  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const orgsQuery = useAdvisoryAiOrgs({ clientId, companyName: search, typeId: XERO_ACCOUNTING_TYPE_ID })
  const rows = useMemo(() => orgsQuery.data ?? [], [orgsQuery.data])
  const saveProfileMutation = useUpdateAdvisoryAiOrgInternalMemoryMutation(editingOrgId ?? '', clientId)

  // Only 'empty' blocks. 'unknown' means the grid has not started reporting the
  // field yet, and treating that as missing would bar every analysis.
  const needsProfile = useCallback(
    (orgId: string, status: IndustryProfileStatus) => status === 'empty' && !justFilled.has(orgId),
    [justFilled]
  )

  const missingProfileNames = useMemo(
    () =>
      [...selected.entries()]
        .filter(([orgId, entry]) => needsProfile(orgId, entry.status))
        .map(([, entry]) => entry.orgName),
    [selected, needsProfile]
  )

  const selectedCount = selected.size
  const canCreate =
    title.trim().length > 0 && selectedCount >= 2 && missingProfileNames.length === 0 && !isCreating

  const toggle = (orgId: string, orgName: string, status: IndustryProfileStatus) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(orgId)) next.delete(orgId)
      else next.set(orgId, { orgName, status })
      return next
    })
  }

  const startEditing = (orgId: string) => {
    setEditingOrgId(orgId)
    setDraft('')
  }

  const cancelEditing = () => {
    setEditingOrgId(null)
    setDraft('')
  }

  const handleSaveProfile = async (orgId: string, orgName: string) => {
    const text = draft.trim()
    if (!text) return
    try {
      await saveProfileMutation.mutateAsync({ orgId, clientId, internalMemory: text })
      setJustFilled((prev) => new Set(prev).add(orgId))
      cancelEditing()
      // The hook invalidates the org's own keys; the company list is a separate
      // query and would otherwise keep serving the stale 'empty' status.
      await queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiOrgsPrefix() })
      addToast(`Industry and Profile saved for ${orgName}`, 'success')
    } catch (error) {
      addToast(
        error instanceof Error ? error.message : 'Failed to save Industry and Profile',
        'error'
      )
    }
  }

  const handleCreate = () => {
    if (!canCreate) return
    const companies = [...selected.entries()].map(([orgId, entry]) => ({ orgId, orgName: entry.orgName }))
    onCreate(title.trim(), companies)
  }

  const handleAfterClose = () => {
    setTitle('')
    setSearch('')
    setSelected(new Map())
    setJustFilled(new Set())
    cancelEditing()
  }

  const footerHint = useMemo(() => {
    if (selectedCount === 0) return 'Select at least 2 companies'
    if (selectedCount === 1) return '1 company selected — select at least 2'
    const missing = missingProfileNames.length
    if (missing > 0) {
      return `${selectedCount} companies selected — ${missing} still ${missing === 1 ? 'needs' : 'need'} Industry and Profile`
    }
    return `${selectedCount} companies selected`
  }, [selectedCount, missingProfileNames])

  return (
    <Modal
      title="New consolidated analysis"
      open={open}
      onCancel={onCancel}
      afterClose={handleAfterClose}
      onOk={handleCreate}
      okText="Create analysis"
      okButtonProps={{ disabled: !canCreate, loading: isCreating }}
      cancelButtonProps={{ disabled: isCreating }}
      width={560}
      maskClosable={!isCreating}
      closable={!isCreating}
    >
      <p className="text-sm text-gray-600 -mt-1 mb-4">
        Select the companies to include. One agenda &amp; analysis runs across all of them.
      </p>

      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. Q4 Portfolio Review"
        maxLength={120}
        className="mb-4"
      />

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search companies…"
        prefix={<SearchOutlined />}
        allowClear
        className="mb-3"
      />

      <div className="max-h-72 overflow-y-auto pr-1 space-y-2">
        {orgsQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <Empty description="No companies found" />
        ) : (
          rows.map((row) => {
            const orgId = row.companyId
            const orgName = row.companyName || row.name
            const checked = selected.has(orgId)
            const missing = needsProfile(orgId, row.industryProfileStatus)
            const isEditing = editingOrgId === orgId
            return (
              <div
                key={row.id || orgId}
                className={`rounded-lg border transition-colors ${
                  checked ? 'feature-selection-card--selected' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 p-3">
                  <Checkbox
                    checked={checked}
                    onChange={() => toggle(orgId, orgName, row.industryProfileStatus)}
                    className="flex flex-1 min-w-0 items-center"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 text-sm truncate">{orgName}</div>
                      {missing ? (
                        <div className="text-xs text-amber-700 truncate">
                          Needs Industry and Profile before it can be included
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 truncate">{row.platformTypeLabel}</div>
                      )}
                    </div>
                  </Checkbox>
                  {missing && !isEditing ? (
                    // Disabled mid-save on purpose: the save mutation binds its cache
                    // keys to editingOrgId, so switching rows while one is in flight
                    // would invalidate the wrong organisation and leave this one stale.
                    <Button
                      size="small"
                      onClick={() => startEditing(orgId)}
                      disabled={isCreating || saveProfileMutation.isPending}
                    >
                      Add
                    </Button>
                  ) : null}
                  {row.refreshStatus ? (
                    <Tag color={platformRefreshStatusTagColor(row.refreshStatus)}>{row.refreshStatus}</Tag>
                  ) : null}
                </div>

                {isEditing ? (
                  <div className="border-t border-gray-100 p-3">
                    <label
                      htmlFor={`profile-${orgId}`}
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Industry and Profile for {orgName}
                    </label>
                    <Input.TextArea
                      id={`profile-${orgId}`}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={4}
                      maxLength={4000}
                      showCount
                      autoFocus
                      placeholder="Add industry and profile details for this organisation"
                    />
                    {/* showCount hangs the counter 22px below the textarea, outside its box,
                        so the row below needs to clear it or the two collide. */}
                    <div className="flex justify-end gap-2 mt-6">
                      <Button
                        size="small"
                        onClick={cancelEditing}
                        disabled={saveProfileMutation.isPending}
                      >
                        Cancel
                      </Button>
                      {/* No feature-button-solid here: it resolves --feature-primary, which
                          FeatureThemeProvider sets on a wrapper div the Modal portals out of,
                          leaving the button with an !important background of nothing. */}
                      <Button
                        size="small"
                        type="primary"
                        loading={saveProfileMutation.isPending}
                        disabled={!draft.trim()}
                        onClick={() => void handleSaveProfile(orgId, orgName)}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })
        )}
      </div>

      <div className="mt-3 text-sm text-gray-500">{footerHint}</div>
    </Modal>
  )
}
